<script setup lang="ts">
import { computed, ref } from "vue";
import { useElementWidth } from "../composables/useElementWidth";
import { formatBucketTime, formatGrouped } from "../format";
import { linearTicks, timeTicks } from "../series";

export interface ChartSeries {
  id: string;
  name: string;
  /** Any CSS colour; normally one of the series or phase custom properties. */
  color: string;
  values: ReadonlyArray<number | null>;
}

/**
 * Time-series chart over pre-bucketed data: one zero-based axis, thin lines (or stacked
 * bands), and a crosshair that reads out every series at the hovered bucket.
 */
const props = withDefaults(
  defineProps<{
    /** Bucket start times in milliseconds, evenly spaced by `stepMs`. */
    times: ReadonlyArray<number>;
    stepMs: number;
    series: ReadonlyArray<ChartSeries>;
    /** Stack the series as bands whose top edge is their sum. */
    stacked?: boolean;
    /** Failed probes and total probes per bucket, drawn as marks under the baseline. */
    failed?: ReadonlyArray<number | null>;
    samples?: ReadonlyArray<number | null>;
    unit?: string;
    digits?: number;
    height?: number;
    label: string;
    /** Newer data is on its way; keep the frame and dim the old curve. */
    busy?: boolean;
  }>(),
  { unit: "", digits: 0, height: 220 }
);

const TOP = 12;
const BOTTOM = 26;
const RIGHT = 8;

const root = ref<HTMLElement | null>(null);
const width = useElementWidth(root);
const active = ref<number | null>(null);
const spoken = ref("");

const count = computed(() => props.times.length);

/** Per-bucket sum of all series, null where any of them is missing. */
const totals = computed(() =>
  props.times.map((_, index) => {
    let sum = 0;
    for (const item of props.series) {
      const value = item.values[index];
      if (value == null) return null;
      sum += value;
    }
    return sum;
  })
);

const peak = computed(() => {
  let max: number | null = null;
  const consider = (value: number | null | undefined) => {
    if (value != null && (max == null || value > max)) max = value;
  };
  if (props.stacked) totals.value.forEach(consider);
  else for (const item of props.series) item.values.forEach(consider);
  return max as number | null;
});

function tickText(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** "12 ms", but "12%": a percent sign sits against its figure. */
function withUnit(text: string): string {
  if (!props.unit) return text;
  return props.unit === "%" ? `${text}%` : `${text} ${props.unit}`;
}

const scale = computed(() => {
  // Readings shown without decimals (events, sockets, milliseconds) get whole-number ticks.
  const values = linearTicks(peak.value ?? 1, props.height < 190 ? 3 : 5, props.digits === 0);
  const top = values[values.length - 1];
  const labels = values.map((value, index) =>
    index === values.length - 1 ? withUnit(tickText(value)) : tickText(value)
  );
  const left = Math.ceil(Math.max(...labels.map((label) => label.length)) * 6.7) + 14;
  return { values, labels, top, left };
});

const plot = computed(() => {
  const left = scale.value.left;
  const plotWidth = Math.max(0, width.value - left - RIGHT);
  const plotHeight = props.height - TOP - BOTTOM;
  const step = count.value ? plotWidth / count.value : 0;
  return {
    left,
    right: left + plotWidth,
    width: plotWidth,
    height: plotHeight,
    floor: TOP + plotHeight,
    step,
    x: (index: number) => left + (index + 0.5) * step,
    y: (value: number) => TOP + plotHeight * (1 - value / scale.value.top)
  };
});

const yTicks = computed(() =>
  scale.value.values.map((value, index) => ({
    value,
    label: scale.value.labels[index],
    y: Math.round(plot.value.y(value)) + 0.5
  }))
);

const xTicks = computed(() => {
  if (!count.value || plot.value.width <= 0) return [];
  const start = props.times[0];
  const end = props.times[count.value - 1] + props.stepMs;
  const { left, right, width: plotWidth } = plot.value;
  return timeTicks(start, end, Math.max(2, Math.floor(plotWidth / 92))).map((tick) => {
    const x = left + ((tick.time - start) / (end - start)) * plotWidth;
    return {
      ...tick,
      x,
      anchor: x - left < 20 ? "start" : right - x < 20 ? "end" : "middle"
    };
  });
});

function linePath(values: ReadonlyArray<number | null>): string {
  const { x, y } = plot.value;
  let path = "";
  let run = 0;
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value == null) {
      // A lone sample between two gaps still deserves a visible dot.
      if (run === 1) path += "h0.01";
      run = 0;
      continue;
    }
    path += `${run === 0 ? "M" : "L"}${x(index).toFixed(1)} ${y(value).toFixed(1)}`;
    run += 1;
  }
  if (run === 1) path += "h0.01";
  return path;
}

/** Closed band between `upper` and `lower` (or the baseline), one polygon per unbroken run. */
function bandPath(
  upper: ReadonlyArray<number | null>,
  lower: ReadonlyArray<number | null> | null
): string {
  const { x, y, floor } = plot.value;
  let path = "";
  let start = -1;
  const close = (end: number) => {
    let forward = "";
    let backward = "";
    for (let index = start; index <= end; index += 1) {
      forward += `${index === start ? "M" : "L"}${x(index).toFixed(1)} ${y(upper[index] as number).toFixed(1)}`;
      const base = lower ? y(lower[index] as number) : floor;
      backward = `L${x(index).toFixed(1)} ${base.toFixed(1)}${backward}`;
    }
    path += `${forward}${backward}Z`;
  };
  for (let index = 0; index < upper.length; index += 1) {
    const present = upper[index] != null && (lower == null || lower[index] != null);
    if (present && start < 0) start = index;
    if (!present && start >= 0) {
      close(index - 1);
      start = -1;
    }
  }
  if (start >= 0) close(upper.length - 1);
  return path;
}

const layers = computed(() => {
  if (!count.value || plot.value.width <= 0) return [];
  if (!props.stacked) {
    return props.series.map((item) => ({
      id: item.id,
      color: item.color,
      line: linePath(item.values),
      band: props.series.length === 1 ? bandPath(item.values, null) : ""
    }));
  }
  let lower: Array<number | null> = totals.value.map((total) => (total == null ? null : 0));
  return props.series.map((item) => {
    const upper = lower.map((base, index) =>
      base == null || item.values[index] == null ? null : base + (item.values[index] as number)
    );
    const layer = { id: item.id, color: item.color, line: "", band: bandPath(upper, lower) };
    lower = upper;
    return layer;
  });
});

const marks = computed(() => {
  const failed = props.failed;
  if (!failed || plot.value.width <= 0) return [];
  const markWidth = Math.max(2, Math.min(6, plot.value.step - 1));
  const result: Array<{ key: number; x: number; width: number; opacity: number }> = [];
  failed.forEach((value, index) => {
    if (value == null || !(value > 0)) return;
    const total = props.samples?.[index];
    const share = total ? value / total : 1;
    result.push({
      key: index,
      x: plot.value.x(index) - markWidth / 2,
      width: markWidth,
      opacity: 0.4 + 0.6 * Math.min(1, share)
    });
  });
  return result;
});

const hasData = computed(() => peak.value != null);

function valueText(value: number | null | undefined): string {
  return value == null ? "无数据" : withUnit(formatGrouped(value, props.digits));
}

const readout = computed(() => {
  const index = active.value;
  if (index == null || index >= count.value || !hasData.value) return null;
  const x = plot.value.x(index);
  let base = 0;
  const rows = props.series.map((item) => {
    const value = item.values[index];
    const top = props.stacked ? (totals.value[index] == null ? null : (base += value as number)) : value;
    return { id: item.id, name: item.name, color: item.color, value, y: top == null ? null : plot.value.y(top) };
  });
  const failed = props.failed?.[index] ?? 0;
  return {
    x,
    time: formatBucketTime(props.times[index], props.stepMs),
    // Read stacked bands top-down, the way they are drawn.
    rows: props.stacked ? [...rows].reverse() : rows,
    total: props.stacked ? totals.value[index] : null,
    failed: failed > 0 ? { count: failed, samples: props.samples?.[index] ?? 0 } : null,
    side: x > width.value / 2 ? "left" : "right"
  };
});

const tipStyle = computed(() => {
  const current = readout.value;
  if (!current) return {};
  return current.side === "left"
    ? { right: `${width.value - current.x + 14}px` }
    : { left: `${current.x + 14}px` };
});

function failedText(failed: { count: number; samples: number }): string {
  const whole = Number.isInteger(failed.count);
  if (whole && failed.samples) return `${failed.count} / ${failed.samples} 次探测失败`;
  return `约 ${(Math.min(1, failed.samples ? failed.count / failed.samples : 1) * 100).toFixed(0)}% 探测失败`;
}

function indexAt(clientX: number): number | null {
  const element = root.value;
  if (!element || !count.value || plot.value.width <= 0) return null;
  const ratio = (clientX - element.getBoundingClientRect().left - plot.value.left) / plot.value.width;
  return Math.min(count.value - 1, Math.max(0, Math.floor(ratio * count.value)));
}

function onPointer(event: PointerEvent) {
  active.value = indexAt(event.clientX);
  // A tap has no hover to end it, so it takes focus and the readout stays until focus leaves.
  if (event.type === "pointerdown" && event.pointerType !== "mouse") {
    root.value?.focus({ preventScroll: true });
  }
}

function onLeave(event: PointerEvent) {
  if (event.pointerType === "mouse") active.value = null;
}

function onKey(event: KeyboardEvent) {
  if (!count.value) return;
  const last = count.value - 1;
  const current = active.value ?? last;
  let next: number;
  if (event.key === "ArrowLeft") next = Math.max(0, current - 1);
  else if (event.key === "ArrowRight") next = Math.min(last, current + 1);
  else if (event.key === "Home") next = 0;
  else if (event.key === "End") next = last;
  else if (event.key === "Escape") {
    active.value = null;
    return;
  } else return;
  event.preventDefault();
  active.value = next;
  const parts = props.series.map((item) => `${item.name} ${valueText(item.values[next])}`);
  spoken.value = `${formatBucketTime(props.times[next], props.stepMs)}，${parts.join("，")}`;
}
</script>

<template>
  <figure
    ref="root"
    class="chart"
    :class="{ 'chart--busy': busy }"
    :style="{ height: `${height}px` }"
    role="img"
    tabindex="0"
    :aria-label="`${label}。按左右方向键逐点读取。`"
    @keydown="onKey"
    @blur="active = null"
  >
    <svg
      v-if="width > 0 && hasData"
      :width="width"
      :height="height"
      :viewBox="`0 0 ${width} ${height}`"
      @pointermove="onPointer"
      @pointerdown="onPointer"
      @pointerleave="onLeave"
    >
      <g class="chart__grid">
        <template v-for="tick in yTicks" :key="tick.value">
          <line
            :class="{ 'chart__baseline': tick.value === 0 }"
            :x1="plot.left"
            :x2="plot.right"
            :y1="tick.y"
            :y2="tick.y"
          />
          <text :x="plot.left - 10" :y="tick.y" dy="0.34em" text-anchor="end">{{ tick.label }}</text>
        </template>
        <text
          v-for="tick in xTicks"
          :key="tick.time"
          :x="tick.x"
          :y="height - 6"
          :text-anchor="tick.anchor"
        >
          {{ tick.label }}
        </text>
      </g>

      <g v-for="layer in layers" :key="layer.id" :style="{ color: layer.color }">
        <path v-if="layer.band" class="chart__band" :class="{ 'chart__band--solid': stacked }" :d="layer.band" />
        <path v-if="layer.line" class="chart__line" :d="layer.line" />
      </g>

      <rect
        v-for="mark in marks"
        :key="mark.key"
        class="chart__mark"
        :x="mark.x"
        :y="plot.floor + 3"
        :width="mark.width"
        height="4"
        rx="1"
        :opacity="mark.opacity"
      />

      <g v-if="readout" class="chart__cursor">
        <line :x1="readout.x" :x2="readout.x" :y1="TOP - 4" :y2="plot.floor" />
        <template v-for="row in readout.rows" :key="row.id">
          <circle v-if="row.y != null" :cx="readout.x" :cy="row.y" r="4" :style="{ fill: row.color }" />
        </template>
      </g>
    </svg>
    <p v-else-if="width > 0" class="chart__empty"><slot name="empty">这个时间范围还没有数据。</slot></p>

    <div v-if="readout" class="chart__tip" :style="tipStyle">
      <p class="chart__tip-time">{{ readout.time }}</p>
      <p v-if="readout.total != null" class="chart__tip-row">
        <strong class="num">{{ valueText(readout.total) }}</strong>
        <span>合计</span>
      </p>
      <p v-for="row in readout.rows" :key="row.id" class="chart__tip-row">
        <i :style="{ background: row.color }" aria-hidden="true"></i>
        <strong class="num">{{ valueText(row.value) }}</strong>
        <span>{{ row.name }}</span>
      </p>
      <p v-if="readout.failed" class="chart__tip-row chart__tip-row--failed">
        <i aria-hidden="true"></i>
        <span>{{ failedText(readout.failed) }}</span>
      </p>
    </div>
    <p class="visually-hidden" aria-live="polite">{{ spoken }}</p>
  </figure>
</template>

<style scoped>
.chart {
  position: relative;
  width: 100%;
  min-width: 0;
  border-radius: var(--radius-small);
  /* Horizontal drags scrub the chart; vertical ones still scroll the page. */
  touch-action: pan-y;
}
.chart svg { display: block; overflow: visible; }
.chart--busy svg { opacity: 0.45; }

.chart__grid line { stroke: var(--grid); stroke-width: 1; }
.chart__grid .chart__baseline { stroke: var(--axis); }
.chart__grid text { fill: var(--ink-3); font-size: 0.71875rem; font-variant-numeric: tabular-nums; }

.chart__line {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.75;
  stroke-linejoin: round;
  stroke-linecap: round;
}
.chart__band { fill: currentColor; fill-opacity: 0.1; }
.chart__band--solid { fill-opacity: 1; }
.chart__mark { fill: var(--bad); }

.chart__cursor line { stroke: var(--axis); stroke-width: 1; }
.chart__cursor circle { stroke: var(--bg); stroke-width: 2; }

.chart__empty {
  display: grid;
  place-items: center;
  height: 100%;
  color: var(--ink-3);
  font-size: 0.875rem;
}

.chart__tip {
  position: absolute;
  top: 0;
  z-index: 2;
  display: grid;
  gap: 0.2rem;
  min-width: 8.5rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--rule);
  border-radius: var(--radius-small);
  background: var(--surface);
  box-shadow: var(--shadow-pop);
  font-size: 0.8125rem;
  line-height: 1.35;
  white-space: nowrap;
  pointer-events: none;
}
.chart__tip-time { margin-bottom: 0.1rem; color: var(--ink-3); font-size: 0.75rem; }
.chart__tip-row { display: flex; gap: 0.45rem; align-items: center; color: var(--ink-2); }
.chart__tip-row strong { color: var(--ink); font-weight: 600; }
.chart__tip-row i { flex: 0 0 auto; width: 0.7rem; height: 2px; border-radius: 1px; }
.chart__tip-row--failed i { width: 0.35rem; height: 0.35rem; background: var(--bad); }

@media (prefers-reduced-motion: no-preference) {
  .chart svg { transition: opacity 160ms ease; }
}
</style>
